import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { useRouter } from 'next/router';
import { useMount, useUnmount } from 'react-use';
import { shallowEqual } from 'react-redux';
import { getEntitlements, getWindow } from '@surfline/web-common';

import HeaderAd from 'components/HeaderAd';
import useMutationObserver from 'hooks/useMutationObserver';
import { getIsSubscribed, useUserPermissionStatus, useUserDetails } from 'selectors/user';
import getHeroAdConfigForPath, { HeroAdConfig } from 'utils/adConfigPaths';
import { getIsPremiumWithAdsUser } from 'utils/user';
import { useAppSelector } from 'stores/hooks';
import convertConditionsBasedAdUnitsToAdTargets from 'utils/conditionsBasedAdUnits';
import { getConditionsBasedAdUnits } from 'selectors/spot';

const AppHeaderAd = () => {
  const router = useRouter();
  const { hasAdFreePermissions } = useUserPermissionStatus();
  const isSubscribed = useAppSelector(getIsSubscribed);
  const entitlements = useAppSelector(getEntitlements, shallowEqual);
  const isPremiumWithAdsUser = getIsPremiumWithAdsUser(entitlements);
  const conditionsBasedAdUnits = useAppSelector(getConditionsBasedAdUnits);
  const conditionsTargets = convertConditionsBasedAdUnitsToAdTargets(conditionsBasedAdUnits);
  const headerAdRef = useRef<HTMLDivElement>(null);
  const [headerAdHeight, setHeaderAdHeight] = useState(0);
  const previousAdConfig = useRef<HeroAdConfig | undefined>(undefined);
  const previousAdConfigSet = useRef<boolean>(false);
  const [adKey, setAdKey] = useState('');
  const win = getWindow();
  const userDetails = useUserDetails();

  const activeHeroAdConfig = useMemo(() => {
    if (router.query?.native === 'true') return undefined;
    const adConfig = getHeroAdConfigForPath(
      router.pathname,
      !!router.query?.qaFlag,
      conditionsTargets,
    );
    if (previousAdConfigSet.current === false) {
      previousAdConfig.current = adConfig;
      previousAdConfigSet.current = true;
    }
    return adConfig;
  }, [router.pathname, router.query?.native, router.query?.qaFlag, conditionsTargets]);

  // Should only display on certain paths that will have a hero ad
  const showHeroAd = useMemo(() => {
    if (hasAdFreePermissions) return false;
    if (isPremiumWithAdsUser && activeHeroAdConfig) return true;
    if (isSubscribed && activeHeroAdConfig?.showToPremium) return true;
    if (!isSubscribed && activeHeroAdConfig) return true;
    return false;
  }, [activeHeroAdConfig, hasAdFreePermissions, isSubscribed, isPremiumWithAdsUser]);

  const headerAdStyles = useMemo(() => {
    if (
      hasAdFreePermissions ||
      !activeHeroAdConfig ||
      (isSubscribed && !isPremiumWithAdsUser && !activeHeroAdConfig?.showToPremium) ||
      !activeHeroAdConfig.reserveHeight
    ) {
      return {
        height: 'initial',
      };
    }
    return {
      height: headerAdHeight === 0 ? 'initial' : `${headerAdHeight}px`,
    };
  }, [
    activeHeroAdConfig,
    hasAdFreePermissions,
    headerAdHeight,
    isSubscribed,
    isPremiumWithAdsUser,
  ]);

  const headerAdClassNames = useMemo(() => {
    // shows ad area with reserved height as ad loads to avoid cumulative layout shift
    if (showHeroAd && activeHeroAdConfig?.reserveHeight !== false) return 'active';
    // passively show ad not reserving height as we are not sure if ad will load
    if (showHeroAd && activeHeroAdConfig?.reserveHeight === false) return 'passive';
    // no ad should be shown
    return '';
  }, [activeHeroAdConfig?.reserveHeight, showHeroAd]);

  const handleAdContainerSize = useCallback(() => {
    const iframe = headerAdRef?.current?.querySelector('iframe');
    const iframeHeight = iframe?.offsetHeight;
    if (iframeHeight && iframeHeight > headerAdHeight) setHeaderAdHeight(iframeHeight);
  }, [headerAdHeight]);

  useMutationObserver(headerAdRef, handleAdContainerSize);

  const handleRouteChangeComplete = useCallback(() => {
    if (
      previousAdConfig.current &&
      activeHeroAdConfig?.adConfigName !== previousAdConfig.current.adConfigName
    ) {
      setAdKey(activeHeroAdConfig?.adConfigName || '');
    }
    previousAdConfig.current = activeHeroAdConfig;
  }, [activeHeroAdConfig, previousAdConfig]);

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [handleRouteChangeComplete, router.events]);

  useMount(() => {
    if (win) win.addEventListener('resize', throttle(handleAdContainerSize, 250));
  });

  useUnmount(() => {
    if (win) win.removeEventListener('resize', throttle(handleAdContainerSize, 250));
  });

  return (
    <div
      id="sl-header-ad"
      data-testid="app-header-ad"
      className={headerAdClassNames}
      style={headerAdStyles}
      ref={headerAdRef}
    >
      {showHeroAd && !!activeHeroAdConfig?.adConfigName && !!activeHeroAdConfig?.adTarget && (
        <HeaderAd
          adConfigName={activeHeroAdConfig?.adConfigName}
          adTarget={activeHeroAdConfig?.adTarget}
          adKey={adKey}
          entitlements={entitlements}
          userDetails={!!userDetails}
        />
      )}
    </div>
  );
};

export default AppHeaderAd;
